import React, { PropsWithChildren, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/store";
import Loader from "components/loader";

import { authenticate } from "./thunk";
import { useOnAuthHandler } from "./hooks";

/**
 * Authentication component to render children after authenticating user
 */
const AuthN: React.FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation();
  const authenticatingLabel = t("features.auth.authenticating");
  const dispatch = useAppDispatch();
  const { isAuthenticated, expiration } = useAppSelector((state) => state.auth);
  const AUTH_EXPIRATION_KEY = "auth_expiration";
  const AUTH_EXPIRY_THRESHOLD_SECS = 900;
  const AUTH_EXPIRY_CHECK_INTERVAL_MILLIS = 120000;

  useOnAuthHandler();

  useEffect(() => {
    if (expiration > 0) {
      // Store expiration in localStorage when it changes in state
      localStorage.setItem(AUTH_EXPIRATION_KEY, expiration.toString());
      const interval = setInterval(() => {
        const authExpiration = Number(
          localStorage.getItem(AUTH_EXPIRATION_KEY) || expiration
        );
        const currentTime = Math.floor(Date.now() / 1000);
        const remainingTime = authExpiration - currentTime;
        if (remainingTime <= AUTH_EXPIRY_THRESHOLD_SECS) {
          dispatch(authenticate(true));
          clearInterval(interval);
        }
      }, AUTH_EXPIRY_CHECK_INTERVAL_MILLIS);
      // Cleanup interval on component unmount
      return () => clearInterval(interval);
    } else {
      dispatch(authenticate(false));
    }
  }, [dispatch, expiration]);

  if (isAuthenticated) {
    return <React.Fragment>{children}</React.Fragment>;
  } else {
    return <Loader loadingText={authenticatingLabel} />;
  }
};

export default AuthN;
